import { shallowRef, type VNode } from 'vue'

export type DialogAction = 'confirm' | 'cancel' | 'close'

type DialogError = {
  action: DialogAction
}

export type ShadDialogOptions = {
  mandatory?: boolean
  confirmButtonText?: string
  cancelButtonText?: string
  closeOnClickModal?: boolean
  distinguishCancelAndClose?: boolean
  autofocus?: boolean
  type?: string
  inputType?: string
  inputPlaceholder?: string
  inputValidator?: (value: string) => boolean | string
  variant?: 'default' | 'destructive'
}

export type ShadDialogRequest = {
  id: number
  open: boolean
  kind: 'confirm' | 'alert' | 'prompt'
  title: string
  message?: string | VNode
  options: ShadDialogOptions
  resolve: (value?: unknown) => void
  reject: (error: DialogError) => void
}

export const shadDialogRequest = shallowRef<ShadDialogRequest | null>(null)

let nextDialogId = 0

const createDialog = <T>(
  kind: ShadDialogRequest['kind'],
  message: string | VNode,
  title: string,
  options: ShadDialogOptions = {},
) =>
  new Promise<T>((resolve, reject) => {
    shadDialogRequest.value = {
      id: (nextDialogId += 1),
      open: true,
      kind,
      title,
      message,
      options,
      resolve: resolve as (value?: unknown) => void,
      reject,
    }
  })

export const shadDialog = {
  confirm: (message: string | VNode, title: string, options?: ShadDialogOptions) =>
    createDialog<void>('confirm', message, title, options),
  alert: (message: string | VNode, title: string, options?: ShadDialogOptions) =>
    createDialog<void>('alert', message, title, options),
  prompt: (message: string | VNode, title: string, options?: ShadDialogOptions) =>
    createDialog<{ value: string }>('prompt', message, title, options),
}
